import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import {
    Home,
    Building2,
    User,
    Layers,
    FileText,
    BarChart2,
    ChevronsRight,
    ChevronsLeft,
    Pin,
    Headphones,
} from "lucide-react";
import XPRSurveyLogo from "../../../assests/XPRSurveyGreen.png";
import { getCompanyLogo } from "../Services/SurveyService";
import ReportIssue from "../../ReportIssue/Pages/ReportIssue";


const NAV_GROUPS = [
    {
        section: "WORKSPACE",
        items: [
            { name: "Dashboard", icon: Home, path: "/dashboard" },
            // { name: "Roles & Permissions", icon: ShieldCheck },
            { name: "Companies", icon: Building2, path: "/dashboard/companies" },
            { name: "Users & Access", icon: User, path: "/dashboard/users" },
            { name: "Channels", icon: Layers, path: "/dashboard/MasterData" },
        ],
    },
    {
        section: "ASSESSMENT",
        items: [
            { name: "Assessments", icon: FileText, path: "/dashboard/Assessment-list" },
            { name: "Reports", icon: BarChart2, path: "/dashboard/reports" },
        ],
    },
    {
        section: "HELP & SUPPORT",
        items: [{ name: "Support", icon: Headphones, path: "/support" }],
    },
];

const NavItem = ({ item, isActive, isExpanded, onClick }) => {
    const Icon = item.icon;
    const className = `flex h-11 items-center gap-3 rounded-lg text-sm font-medium transition-all duration-200 
                group-hover:w-full group-hover:justify-start group-hover:px-3
                ${isExpanded ? "w-full justify-start px-3" : "w-11 justify-center"} 
                ${isActive ? "bg-[#c2a8f033] text-[#7f35b2]" : "text-[#000000] hover:bg-gray-50"}
            `;
    const content = (
        <>
            <Icon size={18} className="shrink-0 text-[#7f35b2]" />
            <span className={`whitespace-nowrap text-[80%] ${isExpanded ? "inline" : "hidden group-hover:inline"}`}>
                {item.name}
            </span>
        </>
    );

    if (onClick) {
        return (
            <button type="button" onClick={onClick} title={item.name} className={`w-full ${className}`}>
                {content}
            </button>
        );
    }

    return (
        <Link
            to={item.path}
            title={item.name}
            aria-current={isActive ? "page" : undefined}
            className={className}
        >
            {content}
        </Link>
    );
};

const Sidebar = () => {
    const location = useLocation();
    const mainGroups = NAV_GROUPS.slice(0, -1);
    const accountGroup = NAV_GROUPS[NAV_GROUPS.length - 1];

    const [isOpen, setIsOpen] = useState(false);
    const [isPinned, setIsPinned] = useState(false);
    const [companyLogoUrl, setCompanyLogoUrl] = useState(null);
    const [showReportIssue, setShowReportIssue] = useState(false);

    const isExpanded = isOpen || isPinned;

    // Auto-close sidebar on route change when on mobile
    useEffect(() => {
        setIsOpen(false);
    }, [location.pathname]);

    useEffect(() => {
        let objectUrl;

        const fetchLogo = async () => {
            try {
                const blob = await getCompanyLogo();
                objectUrl = URL.createObjectURL(blob);
                setCompanyLogoUrl(objectUrl);
            } catch (err) {
                if (err.status === 404) {
                    CustomToast("fail", "Company logo not found.");
                } else {
                    CustomToast("fail", "Something went wrong while fetching the company logo.");
                }
            }
        };

        fetchLogo();

        return () => {
            if (objectUrl) URL.revokeObjectURL(objectUrl);
        };
    }, []);


    const handleReportIssue = () => setShowReportIssue(true);

    return (
        <>
            {isOpen && (
                <div
                    className="fixed inset-0 z-[50] bg-black/20 md:hidden"
                    onClick={() => setIsOpen(false)}
                    aria-hidden="true"
                />
            )}

            <aside
                className={`group fixed md:sticky left-0 top-0 z-[60] flex h-screen shrink-0 flex-col
                overflow-hidden border-r border-gray-100 bg-white shadow-lg transition-all
                duration-300 ease-in-out w-[270px]
                ${isPinned ? "md:w-[270px]" : "md:w-[72px] hover:md:w-[270px]"}
                ${isOpen ? "translate-x-0" : "-translate-x-full md:translate-x-0"}`}
            >
                {/* Sidebar Header */}
                <div className="flex h-20 shrink-0 items-center justify-between gap-2 overflow-hidden px-4">
                    <div className="flex items-center gap-3 overflow-hidden">

                        {companyLogoUrl && (
                            <img
                                src={companyLogoUrl}
                                alt="Company Logo"
                                title="Company Logo"
                                className={`shrink-0 rounded-md object-cover transition-all duration-300 ${isExpanded ? "w-16" : "w-9 group-hover:w-16"}`}
                            />
                        )}
                        <img
                            src={XPRSurveyLogo}
                            alt="XPR Survey"
                            className={`shrink-0 transition-all duration-300 ${isExpanded ? "w-20" : "w-9 group-hover:w-20"}`}
                        />
                        {/* <span className={`whitespace-nowrap text-base font-bold leading-tight text-gray-900 ${isExpanded ? "inline" : "hidden group-hover:inline"}`}>
                            Assessment<br />Portal
                        </span> */}
                    </div>

                    {/* Desktop Pin Toggle Button */}
                    <button
                        type="button"
                        onClick={() => setIsPinned((prev) => !prev)}
                        className={`items-center justify-center p-1.5 rounded-lg transition-all duration-200 shrink-0
                            ${isPinned ? "hidden md:flex" : "hidden md:group-hover:flex"} 
                            ${isPinned ? "bg-[#c2a8f033] text-[#7f35b2] hover:bg-[#c2a8f04d]" : "text-gray-400 hover:text-gray-600 hover:bg-gray-100"}
                        `}
                        title={isPinned ? "Unpin sidebar" : "Pin sidebar"}
                    >
                        <Pin
                            size={16}
                            className={`transition-transform duration-200 ${isPinned ? "rotate-45 fill-[#7f35b2]" : ""}`}
                        />
                    </button>
                </div>

                {/* Sidebar Navigation Items */}
                <div className="scrollbar-hide flex flex-1 flex-col overflow-y-auto overflow-x-hidden px-3 py-1">
                    {mainGroups.map((group) => (
                        <div key={group.section} className={`py-2 border-b-[0.1px] border-b-[#7f35b2] ${isExpanded ? "pb-2" : "group-hover:pb-2"}`}>
                            <h4 className={`mb-2 px-3 text-[11px] font-bold tracking-wider text-gray-400 uppercase ${isExpanded ? "block" : "hidden group-hover:block"}`}>
                                {group.section}
                            </h4>
                            <div className="space-y-1">
                                {group.items.map((item) => {
                                    const isActive =
                                        location.pathname === item.path ||
                                        (item.name === "Dashboard" && location.pathname === "/");
                                    return <NavItem key={item.name} item={item} isActive={isActive} isExpanded={isExpanded} />;
                                })}
                            </div>
                        </div>
                    ))}
                    <div className="mt-auto border-t border-gray-100 pt-3">
                        <h4 className={`mb-2 px-3 text-[11px] font-bold tracking-wider text-gray-400 uppercase ${isExpanded ? "block" : "hidden group-hover:block"}`}>
                            {accountGroup.section}
                        </h4>
                        <div className="space-y-1 pb-2">
                            {accountGroup.items.map((item) => {
                                const isActive = location.pathname === item.path;
                                return <NavItem key={item.name} item={item} isActive={isActive} isExpanded={isExpanded} onClick={item.name === "Support" ? handleReportIssue : undefined} />;
                            })}
                        </div>
                    </div>
                </div>
            </aside>

            {/* Mobile Toggle Button */}
            <button
                type="button"
                onClick={() => setIsOpen((prev) => !prev)}
                className="md:hidden fixed top-1/2 -translate-y-1/2 flex items-center justify-center w-5 h-[6rem] bg-white border-[1.5px] border-[#7f35b2] border-l-0 rounded-r-full cursor-pointer shadow-sm z-[60] transition-all duration-300 hover:bg-gray-50"
                style={{
                    left: isOpen ? "270px" : "0",
                }}
                title={isOpen ? "Hide sidebar" : "Show sidebar"}
            >
                {isOpen ? (
                    <ChevronsLeft size={16} className="text-[#7f35b2]" />
                ) : (
                    <ChevronsRight size={16} className="text-[#7f35b2]" />
                )}
            </button>
            {/* Report Issue Modal */}
            {showReportIssue && (
                <div
                    className="fixed inset-0 z-[100] bg-slate-900/40 backdrop-blur-[2px]"
                    onClick={() => setShowReportIssue(false)}
                >
                    <div
                        role="dialog"
                        aria-modal="true"
                        aria-label="Report an issue"
                        className="fixed right-0 top-0 z-[101] flex h-full w-full flex-col overflow-hidden rounded-l-2xl bg-white shadow-2xl md:w-[640px]"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <ReportIssue onClose={() => setShowReportIssue(false)} />
                    </div>
                </div>
            )}
        </>
    );
};

export default Sidebar;
